"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import axios from "axios";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { useAuthStore } from "@/store/auth";
import { useBetaStatus } from "@/hooks/useBetaStatus";
import { LifecycleStage, STAGE_LABEL, STAGE_EMOJI, STAGE_COLOR, STAGE_BG, KeywordDetail, HistoryPoint } from "@/types";
import { SnapshotComparison } from "@/components/trend/SnapshotComparison";

interface SourceDoc {
  source: string;
  title: string | null;
  link: string;
  published_at: string | null;
}
interface SourcesResult {
  total: number;
  sources: SourceDoc[];
}

interface DatalabPoint {
  date: string;
  ratio: number;
}
interface DatalabResponse {
  keyword: string;
  days: number;
  data: {
    series: DatalabPoint[];
    summary: {
      avg: number | null;
      peak: number | null;
      direction: "rising" | "flat" | "falling" | null;
    };
  } | null;
}

// 사용자 결정 2026-05-10 — 옵션 B: 카운트만 노출 + 상세 상품 리스트 제거
// (사유: 같은 제품 다채널 중복으로 카페 점주에게 정크 인상. 1,087개 카운트 = 시장 출시도 시그널만 유지)
interface ShoppingResponse {
  keyword: string;
  current_count: number | null;
}

// "region" 탭 폐기 (사용자 결정 2026-05-09 — Place 시스템 통째 폐기)
type TabKey = "shopping" | "sources" | "recipes" | "suppliers" | "community";

// 2026-05-28 v9 — 카테고리 칩 (product.category 그룹핑). 완제/반제/원료·2분법 폐기.
interface SupplierProductFlat {
  id: number;
  name: string;
  spec: string | null;
  category: string | null;
  product_url: string | null;
  manufacturer_id: number;
  manufacturer_company: string;
  manufacturer_category: string;
  premium_yn: boolean;
}
interface AiIngredient {
  name: string;
  category: string;
}
interface AiRecipe {
  finished: AiIngredient[];
  intermediate: AiIngredient[];
  raw: AiIngredient[];
}
interface SuppliersResult {
  keyword: string;
  total: number;
  supplier_count: number;
  category_counts: Record<string, number>;
  ai_recipe: AiRecipe;
  products: SupplierProductFlat[];
}

const DIRECTION_LABEL: Record<string, { label: string; color: string; emoji: string }> = {
  rising:  { label: "상승",  color: "#FF5C35", emoji: "↗" },
  flat:    { label: "횡보",  color: "#B0B0B0", emoji: "→" },
  falling: { label: "하락",  color: "#60A5FA", emoji: "↘" },
};

const DIFFICULTY: Record<string, string> = { 하: "★☆☆", 중: "★★☆", 상: "★★★" };
const SOURCES_PAGE = 10;

function youtubeId(url: string) {
  const m = url.match(/(?:v=|youtu\.be\/)([^&?/]+)/);
  return m ? m[1] : null;
}

function extractHashtag(url: string): string | null {
  // instagram.com/explore/tags/우베디저트/ → 우베디저트
  const m = url.match(/\/tags\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

interface Props {
  // SSG/ISR — server page.tsx 가 미리 fetch 한 초기 데이터 (없으면 client 가 fetch)
  initialDetail?: KeywordDetail | null;
  initialHistory?: HistoryPoint[];
  initialSources?: SourcesResult | null;
  // Phase S Step 4 v2 (2026-06-06) — 시점 비교 server-fetched data (추이 차트 직후 표시)
  snapshotComparison?: import("@/components/trend/SnapshotComparison").SnapshotComparisonData | null;
}

export default function TrendDetailClient({ initialDetail, initialHistory, initialSources, snapshotComparison }: Props = {}) {
  const { id } = useParams<{ id: string }>();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { token } = useAuthStore();

  // SSG initial data 있으면 즉시 사용 (loading=false), 없으면 client fetch
  const [detail, setDetail] = useState<KeywordDetail | null>(initialDetail ?? null);
  const [history, setHistory] = useState<HistoryPoint[]>(initialHistory ?? []);
  const [sources, setSources] = useState<SourcesResult | null>(initialSources ?? null);
  const [loading, setLoading] = useState(!initialDetail);
  const [error, setError] = useState(false);
  const [savedId, setSavedId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [datalab, setDatalab] = useState<DatalabResponse | null>(null);
  const [datalabLoading, setDatalabLoading] = useState(false);
  const [shopping, setShopping] = useState<ShoppingResponse | null>(null);
  const [shoppingLoading, setShoppingLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<SuppliersResult | null>(null);
  const [suppliersLoading, setSuppliersLoading] = useState(false);

  useEffect(() => {
    // SSG initial 있으면 첫 fetch skip (응답 즉시, hydration 부드러움)
    if (initialDetail) return;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    Promise.all([
      axios.get(`/api/trends/${id}/detail`, { headers }),
      axios.get(`/api/trends/${id}/history`, { headers }),
      axios.get(`/api/trends/${id}/sources`, { headers, params: { limit: SOURCES_PAGE } }),
    ])
      .then(([d, h, s]) => {
        setDetail(d.data);
        setHistory(h.data.history);
        setSources(s.data);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id, token, initialDetail]);

  // 저장 상태 조회 (로그인 & detail 로드된 뒤)
  useEffect(() => {
    if (!token || !detail) return;
    axios
      .get("/api/me/saved/check", {
        headers: { Authorization: `Bearer ${token}` },
        params: { keyword_text: detail.keyword.keyword, channel_id: detail.keyword.channel_id },
      })
      .then((r) => setSavedId(r.data.saved_id))
      .catch(() => {});
  }, [token, detail]);

  // 탭은 모든 키워드에서 동일 구성 (일관성). 콘텐츠가 비어있으면 각 탭 내부에서 안내.
  // 검색지수 탭 제거 (사용자 결정 2026-05-08) — 통합 그래프 + 평균/최고/추세 박스가 같은 정보 노출
  const availableTabs = useMemo<{ key: TabKey; label: string; emoji: string; count: number | null }[]>(() => {
    if (!detail) return [];
    // "지도" 탭 폐기 (사용자 결정 2026-05-09 — Place 시스템 통째 폐기, 4탭으로 축소)
    return [
      { key: "shopping",  label: "쇼핑",     emoji: "🛒", count: detail.keyword.shopping_count ?? null },
      { key: "sources",   label: "원문",     emoji: "📄", count: sources?.total ?? 0 },
      { key: "recipes",   label: "레시피",   emoji: "🍳", count: detail.recipes.length },
      { key: "suppliers", label: "관련제품", emoji: "🏭", count: suppliers?.total ?? detail.keyword.manufacturer_count ?? 0 },
      { key: "community", label: "후기",     emoji: "💬", count: detail.community_preview.length },
    ];
  }, [detail, sources, suppliers]);

  // 기본 탭: URL ?tab= 우선 / 없으면 첫 번째 존재 탭
  useEffect(() => {
    if (activeTab !== null || availableTabs.length === 0) return;
    const requested = searchParams.get("tab") as TabKey | null;
    if (requested && availableTabs.some((t) => t.key === requested)) {
      setActiveTab(requested);
    } else {
      setActiveTab(availableTabs[0].key);
    }
  }, [availableTabs, activeTab, searchParams]);

  // datalab 자동 로드 (사용자 결정 2026-05-08 — 통합 그래프에서 사용, lazy 제거)
  useEffect(() => {
    if (datalab !== null || datalabLoading) return;
    setDatalabLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios
      .get<DatalabResponse>(`/api/trends/${id}/datalab`, { headers })
      .then((r) => setDatalab(r.data))
      .catch(() => setDatalab({ keyword: "", days: 30, data: null }))
      .finally(() => setDatalabLoading(false));
  }, [datalab, datalabLoading, id, token]);

  // shopping 탭 lazy load — 카운트만 사용 (사용자 결정 2026-05-10, 옵션 B)
  // products=1 최소 호출 (백엔드 ge=1 검증 + current_count fallback 위해)
  useEffect(() => {
    if (activeTab !== "shopping" || shopping !== null || shoppingLoading) return;
    setShoppingLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios
      .get<ShoppingResponse>(`/api/trends/${id}/shopping`, { headers, params: { products: 1 } })
      .then((r) => setShopping({ keyword: r.data.keyword, current_count: r.data.current_count }))
      .catch(() => setShopping({ keyword: "", current_count: null }))
      .finally(() => setShoppingLoading(false));
  }, [activeTab, shopping, shoppingLoading, id, token]);

  // suppliers — 2026-05-19 page load 시 자동 fetch (탭 그리드 카운트 정확성).
  // 이전: lazy load (탭 클릭 시) → ISR cached manufacturer_count 와 불일치 가능
  useEffect(() => {
    if (suppliers !== null || suppliersLoading || !detail) return;
    setSuppliersLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios
      .get<SuppliersResult>(`/api/trends/${id}/suppliers`, { headers })
      .then((r) => setSuppliers(r.data))
      .catch(() => setSuppliers({ keyword: "", total: 0, supplier_count: 0, category_counts: {}, ai_recipe: { finished: [], intermediate: [], raw: [] }, products: [] }))
      .finally(() => setSuppliersLoading(false));
  }, [detail, suppliers, suppliersLoading, id, token]);

  const toggleSave = async () => {
    if (!token) {
      router.push("/login");
      return;
    }
    if (!detail || saving) return;
    setSaving(true);
    try {
      if (savedId) {
        await axios.delete(`/api/me/saved/${savedId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSavedId(null);
      } else {
        const r = await axios.post(
          "/api/me/saved",
          { keyword_text: detail.keyword.keyword, channel_id: detail.keyword.channel_id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSavedId(r.data.id);
      }
    } catch {
      // noop
    } finally {
      setSaving(false);
    }
  };

  const loadMoreSources = async () => {
    if (!sources || loadingMore) return;
    setLoadingMore(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const r = await axios.get<SourcesResult>(`/api/trends/${id}/sources`, {
        headers,
        params: { limit: SOURCES_PAGE, offset: sources.sources.length },
      });
      setSources({
        total: r.data.total,
        sources: [...sources.sources, ...r.data.sources],
      });
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin text-4xl">🌀</div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-text-sub">키워드 정보를 불러올 수 없어요.</p>
        <button onClick={() => router.back()} className="mt-4 text-primary text-sm">← 돌아가기</button>
      </div>
    );
  }

  // place_summary 구조분해 폐기 (사용자 결정 2026-05-09 — Place 시스템 통째 폐기)
  const { keyword, recipes, community_preview } = detail;
  const stage = keyword.lifecycle_stage as LifecycleStage | null;
  const stageColor = stage ? STAGE_COLOR[stage] : "#B0B0B0";
  const stageBg = stage ? STAGE_BG[stage] : "rgba(136,136,136,0.15)";
  // β-9 D 항목: declining 세분화 라벨 (bubble = 🫧 거품 / fading = 📉 일반)
  const sub = keyword.lifecycle_substage;
  const stageLabel = stage
    ? (stage === "declining" && sub === "bubble" ? "🫧 거품 단계"
      : stage === "declining" && sub === "fading" ? "📉 일반 하락"
      : `${STAGE_EMOJI[stage]} ${STAGE_LABEL[stage]}`)
    : "미분류";

  // 통합 그래프 데이터 — history (일별 SNS 언급) + datalab (검색지수) date 기준 merge
  // 사용자 결정 2026-05-08:
  //   1. 옛 score (Min-Max) → mention_count (일별 SNS 건수) — emerging 0점 깔림 해소
  //   2. 누락 날 = 0 처리 — 사용자 인지 명확 ("그날 0건이구나")
  //      (옛 connectNulls 만 사용 — 데이터 없는 날 직선 이어져 모호)
  const combinedSeries = useMemo(() => {
    // 모든 날짜 union (history + datalab)
    const allDates = new Set<string>();
    history.forEach((h) => allDates.add(h.date));
    datalab?.data?.series.forEach((d) => allDates.add(d.date));

    const historyByDate = new Map(history.map((h) => [h.date, h.mention_count]));
    const ratioByDate = new Map(
      (datalab?.data?.series ?? []).map((d) => [d.date, d.ratio])
    );

    return Array.from(allDates)
      .sort()
      .map((date) => ({
        date,
        // mention 누락 = 0 처리 (사용자 인지 명확)
        mention: historyByDate.get(date) ?? 0,
        // ratio 누락 = undefined (검색지수 빠진 날은 자연 끊김 — connectNulls 가 보강)
        ratio: ratioByDate.get(date),
      }));
  }, [history, datalab]);

  return (
    <div className="pb-24">
      {/* 헤더 — 1줄 통일 (사용자 결정 2026-05-08) */}
      <div className="sticky top-0 z-40 bg-bg/90 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => router.back()} className="text-text-sub hover:text-text text-xl">←</button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-black text-text truncate flex items-center gap-2">
              <span className="truncate">{keyword.keyword}</span>
              {keyword.is_new && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 shrink-0">
                  🆕
                </span>
              )}
            </h1>
            <div className="flex items-center gap-1.5 mt-1 text-2xs text-text-sub flex-wrap">
              <span
                className="font-bold px-2 py-0.5 rounded-full"
                style={{ color: stageColor, background: stageBg }}
              >
                {stageLabel}
              </span>
              <span>·</span>
              <span>30일 SNS <strong className="text-text">{keyword.mention_count.toLocaleString()}</strong>건</span>
              {keyword.mention_count_7d != null && keyword.mention_count_7d > 0 && (
                <>
                  <span>·</span>
                  <span>7일 SNS <strong className="text-text">{keyword.mention_count_7d.toLocaleString()}</strong>건</span>
                </>
              )}
            </div>
          </div>
          <button
            onClick={toggleSave}
            disabled={saving}
            aria-label={savedId ? "저장 해제" : "저장"}
            className="text-2xl leading-none disabled:opacity-40 transition-transform active:scale-90"
          >
            {savedId ? "★" : "☆"}
          </button>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* AI 판단 자동 요약 — 페이지 진입 시 자동 호출 (사용자 결정 2026-05-08) */}
        <AiSummaryCard keywordId={id} />

        {/* 통합 추이 그래프 — 관심도(자체점수) + 검색지수(데이터랩) 2축 (사용자 결정 2026-05-08) */}
        <section className="card p-4">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-sm font-bold text-text">추이</h2>
            {history.length >= 7 && (() => {
              // SNS 언급 변화율 — 첫주 평균 vs 최근주 평균 (사용자 결정 2026-05-18 — 단순 first/last 의 변동성 큰 한계 해소)
              const firstWeek = history.slice(0, 7);
              const lastWeek = history.slice(-7);
              const firstAvg = firstWeek.reduce((s, h) => s + (h.mention_count ?? 0), 0) / firstWeek.length;
              const lastAvg = lastWeek.reduce((s, h) => s + (h.mention_count ?? 0), 0) / lastWeek.length;
              const change = firstAvg > 0 ? ((lastAvg - firstAvg) / firstAvg) * 100 : 0;
              const changeText = !isFinite(change) ? "—" : change > 0 ? `↑ ${change.toFixed(0)}%` : change < 0 ? `↓ ${Math.abs(change).toFixed(0)}%` : "변동 없음";
              const changeColor = change > 0 ? "#FF5C35" : change < 0 ? "#60A5FA" : "#B0B0B0";
              return (
                <span className="text-2xs" style={{ color: changeColor }} title="첫 주 평균 대비 최근 주 평균 SNS 언급 변화율 (변동성 흡수)">
                  4주 추세 {changeText}
                </span>
              );
            })()}
          </div>
          {/* 네이버 검색지수 요약 박스 (사용자 결정 2026-05-08 — 라벨 + 설명 명시) */}
          {datalab?.data?.summary && (() => {
            const s = datalab.data.summary;
            const dir = s.direction ? DIRECTION_LABEL[s.direction] : null;
            return (
              <div className="mb-3">
                <p className="text-2xs font-semibold text-text-sub mb-1.5">
                  네이버 검색지수 <span className="opacity-70">(최근 4주)</span>
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center py-1.5 rounded-lg bg-bg-secondary">
                    <p className="text-2xs text-text-sub">평균</p>
                    <p className="text-sm font-bold text-text">
                      {s.avg !== null ? s.avg.toFixed(1) : "-"}
                    </p>
                  </div>
                  <div className="text-center py-1.5 rounded-lg bg-bg-secondary">
                    <p className="text-2xs text-text-sub">최고</p>
                    <p className="text-sm font-bold text-text">
                      {s.peak !== null ? s.peak.toFixed(0) : "-"}
                    </p>
                  </div>
                  <div className="text-center py-1.5 rounded-lg bg-bg-secondary">
                    <p className="text-2xs text-text-sub">추세</p>
                    {dir ? (
                      <p className="text-sm font-bold" style={{ color: dir.color }}>
                        {dir.emoji} {dir.label}
                      </p>
                    ) : (
                      <p className="text-sm font-bold text-text-sub">-</p>
                    )}
                  </div>
                </div>
                <p className="text-[10px] text-text-sub/70 mt-1.5 leading-snug">
                  * 100 = 기간 내 최고 검색량 기준 상대 지수 (네이버 데이터랩) — 일반 사용자의 실제 검색량
                </p>
              </div>
            );
          })()}
          {combinedSeries.length > 1 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={combinedSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: "#B0B0B0" }}
                    tickFormatter={(v) => v.slice(5)}
                    interval="preserveStartEnd"
                  />
                  <YAxis yAxisId="left" hide />
                  <YAxis yAxisId="right" orientation="right" hide domain={[0, 100]} />
                  <Tooltip
                    contentStyle={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8 }}
                    labelStyle={{ color: "#B0B0B0", fontSize: 11 }}
                    itemStyle={{ fontSize: 12 }}
                    formatter={(v: number, name: string) => {
                      if (name === "mention") return [`${v ?? 0}건`, "SNS 언급"];
                      if (name === "ratio") return [v?.toFixed(1) ?? "—", "검색지수"];
                      return [v, name];
                    }}
                  />
                  {/* 색상 교체 (사용자 결정 2026-05-09) — 네이버 상징 = 초록 → 검색지수 (네이버 데이터랩) 에 부여 */}
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="mention"
                    stroke="#60A5FA"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: "#60A5FA" }}
                    connectNulls
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="ratio"
                    stroke="#22C55E"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: "#22C55E" }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
              <div className="flex items-center gap-3 mt-2 text-2xs text-text-sub flex-wrap">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5" style={{ background: "#60A5FA" }}></span> SNS 언급 <span className="opacity-60">(일별 건수)</span></span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-0.5" style={{ background: "#22C55E" }}></span> 검색지수 <span className="opacity-60">(네이버 데이터랩 100인 상대지수)</span></span>
              </div>
            </>
          ) : (
            <p className="text-xs text-text-sub text-center py-6">데이터가 충분히 쌓이면 그래프가 표시됩니다.</p>
          )}
        </section>

        {/* 시점 비교 (Phase S Step 4 v2 — 추이 차트 직후) */}
        {snapshotComparison && <SnapshotComparison data={snapshotComparison} />}

        {/* 탭 영역 */}
        {availableTabs.length === 0 ? (
          <div className="card p-6 text-center">
            <p className="text-text-sub text-sm">아직 수집된 상세 데이터가 없어요.</p>
            <p className="text-text-sub text-xs mt-1">수집 파이프라인 실행 후 데이터가 쌓입니다.</p>
          </div>
        ) : (
          <>
            {/* 탭 grid 카드 — 사용자 결정 2026-05-08 (4열, 카운트 강조 + 0 흐림) */}
            <div className="grid grid-cols-4 gap-2">
              {availableTabs.map((tab) => {
                const active = tab.key === activeTab;
                const empty = tab.count !== null && tab.count === 0;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex flex-col items-center justify-center py-2.5 rounded-xl border transition-all ${
                      active
                        ? "bg-bg-card"
                        : "bg-bg-secondary border-transparent hover:bg-bg-card/60"
                    } ${empty ? "opacity-50" : ""}`}
                    style={active ? { borderColor: stageColor } : undefined}
                  >
                    <span className="text-base">{tab.emoji}</span>
                    <span className="text-2xs font-semibold text-text-sub mt-0.5">{tab.label}</span>
                    {tab.count !== null && (
                      <span className={`text-xs font-bold mt-0.5 ${active ? "text-text" : "text-text-sub"}`}>
                        {tab.count.toLocaleString()}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* 탭 콘텐츠 */}
            <section className="card p-4 mt-3">
              {activeTab === "shopping" && (
                <ShoppingTab
                  data={shopping}
                  loading={shoppingLoading}
                  keywordText={keyword.keyword}
                />
              )}

              {activeTab === "sources" && sources && (
                <SourcesTab
                  sources={sources}
                  loadingMore={loadingMore}
                  onLoadMore={loadMoreSources}
                />
              )}

              {/* "region" 탭 렌더링 폐기 (사용자 결정 2026-05-09 — Place 시스템 통째 폐기) */}

              {activeTab === "recipes" && <RecipesTab recipes={recipes} />}

              {activeTab === "suppliers" && (
                <SuppliersTab
                  data={suppliers}
                  loading={suppliersLoading}
                  keywordText={keyword.keyword}
                />
              )}

              {activeTab === "community" && (
                <CommunityTab
                  posts={community_preview}
                  stageColor={stageColor}
                  onMore={() => router.push("/community")}
                />
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────
   탭별 서브 컴포넌트 (파일 내에서만 사용)
   ──────────────────────────────────────────────── */

function SourcesTab({
  sources,
  loadingMore,
  onLoadMore,
}: {
  sources: SourcesResult;
  loadingMore: boolean;
  onLoadMore: () => void;
}) {
  const loaded = sources.sources.length;
  const hasMore = loaded < sources.total;

  if (sources.total === 0) {
    return (
      <p className="text-xs text-text-sub text-center py-6">
        아직 언급된 원문이 없어요
      </p>
    );
  }

  return (
    <>
      <p className="text-xs text-text-sub mb-3">
        최근 30일 기준 총 <span className="text-text font-semibold">{sources.total}건</span>의 원문에서 이 키워드가 언급됐어요.
      </p>
      <div className="space-y-2">
        {sources.sources.map((doc, i) => {
          const isInstagram = doc.source === "인스타그램";
          const hashtag = isInstagram ? extractHashtag(doc.link) : null;
          return (
            <a
              key={i}
              href={doc.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-2 p-2 rounded-xl bg-bg-secondary hover:bg-bg-secondary/80 transition-colors"
            >
              <span className="text-2xs font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary flex-shrink-0 mt-0.5">
                {doc.source}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-text line-clamp-2 leading-relaxed">
                  {doc.title ?? "게시물"}
                </p>
                {isInstagram ? (
                  <p className="text-2xs text-text-sub mt-0.5">
                    📌 {hashtag ? `#${hashtag}` : "해시태그"} 피드로 이동
                  </p>
                ) : (
                  doc.published_at && (
                    <p className="text-2xs text-text-sub mt-0.5">
                      {new Date(doc.published_at).toLocaleDateString("ko-KR")}
                    </p>
                  )
                )}
              </div>
              <span className="text-text-sub text-xs flex-shrink-0">→</span>
            </a>
          );
        })}
      </div>
      {hasMore ? (
        <button
          onClick={onLoadMore}
          disabled={loadingMore}
          className="w-full mt-3 py-2.5 text-xs font-semibold text-text-sub border border-border rounded-xl hover:bg-bg-elevated transition-colors disabled:opacity-50"
        >
          {loadingMore ? "불러오는 중..." : `더 보기 (${sources.total - loaded}건 남음)`}
        </button>
      ) : loaded > SOURCES_PAGE ? (
        <p className="text-2xs text-text-sub text-center mt-3">이게 전부예요 🎉</p>
      ) : null}
    </>
  );
}


// RegionTab + PlacesResult/PlaceItemOut 인터페이스 폐기 (사용자 결정 2026-05-09 — Place 시스템 통째 폐기)

function SuppliersTab({
  data,
  loading,
  keywordText,
}: {
  data: SuppliersResult | null;
  loading: boolean;
  keywordText: string;
}) {
  if (loading) {
    return <p className="text-xs text-text-sub text-center py-6">불러오는 중...</p>;
  }
  if (!data || data.products.length === 0) {
    return (
      <div className="text-center py-6">
        <p className="text-xs text-text-sub">
          &quot;{keywordText}&quot; 관련제품이 아직 매칭되지 않았어요
        </p>
        <Link
          href={`/manufacturers?q=${encodeURIComponent(keywordText)}`}
          className="text-2xs text-primary mt-2 inline-block"
        >
          제조업체 디렉토리에서 검색 →
        </Link>
      </div>
    );
  }

  return <SuppliersTabContent data={data} keywordText={keywordText} />;
}

// 2026-05-28 v9 — 카테고리 칩 (product.category 그룹핑). 완제/반제/원료·2분법 폐기.
const CATEGORY_EMOJI: Record<string, string> = {
  "재료·원료": "🥣", "유제품": "🥛", "베이커리": "🥐",
  "디저트": "🍰", "음료": "🥤", "포장·소모품": "📦", "완제품": "☕", "기타": "📦",
};

function SuppliersTabContent({ data, keywordText }: { data: SuppliersResult; keywordText: string }) {
  // 카테고리 칩 — category_counts (카운트>0) 만, 카운트 많은 순. "전체" 칩 선두.
  const catEntries = Object.entries(data.category_counts)
    .filter(([, c]) => c > 0)
    .sort((a, b) => b[1] - a[1]);
  const [activeCat, setActiveCat] = useState<string>("전체");
  const [search, setSearch] = useState("");

  const norm = (s: string) => s.replace(/\s/g, "").toLowerCase();
  const q = norm(search.trim());
  const filtered = data.products
    .filter((p) => activeCat === "전체" || (p.category || "기타") === activeCat)
    .filter((p) => !q || norm(p.name).includes(q) || norm(p.manufacturer_company).includes(q));

  // AI 분석 base 명사 (intermediate + raw + finished 통합 표시)
  const baseTerms = [...data.ai_recipe.finished, ...data.ai_recipe.intermediate, ...data.ai_recipe.raw];

  return (
    <div>
      <p className="text-2xs text-text-sub mb-2">
        관련제품 <span className="text-text font-semibold">{data.total}개</span>
        {data.supplier_count > 0 && <span className="text-text-sub"> · 공급처 {data.supplier_count}곳</span>}
      </p>

      {/* 카테고리 칩 — 전체 + product.category 별 (빈 칩 미노출) */}
      <div className="flex gap-1.5 mb-2 flex-wrap">
        <button
          type="button"
          onClick={() => setActiveCat("전체")}
          className={`px-2.5 py-1 text-2xs font-bold rounded-full border transition-colors ${
            activeCat === "전체" ? "border-primary bg-primary/10 text-primary" : "border-border text-text-sub"
          }`}
        >
          전체 {data.total}
        </button>
        {catEntries.map(([cat, cnt]) => (
          <button
            key={cat}
            type="button"
            onClick={() => setActiveCat(cat)}
            className={`px-2.5 py-1 text-2xs font-bold rounded-full border transition-colors ${
              activeCat === cat ? "border-primary bg-primary/10 text-primary" : "border-border text-text-sub"
            }`}
          >
            {CATEGORY_EMOJI[cat] || "📦"} {cat} {cnt}
          </button>
        ))}
      </div>

      {/* AI 분석 base 명사 */}
      {baseTerms.length > 0 && (
        <p className="text-2xs text-text-sub mb-2 opacity-80">
          AI 분석: {baseTerms.map((i) => i.name).join(", ")}
        </p>
      )}

      {/* 검색 — 제품명/공급처 substring */}
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="제품명·공급처 검색"
        className="w-full bg-bg-secondary text-text text-xs px-3 py-2 rounded-lg border border-border outline-none focus:border-primary/50 mb-2"
      />

      {filtered.length === 0 ? (
        <p className="text-text-sub text-center py-6 text-xs">
          {search ? "검색 결과가 없어요" : `${activeCat === "전체" ? "관련" : activeCat} 제품이 없어요`}
        </p>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="grid grid-cols-[2fr_1fr_38px] gap-1.5 px-2 py-1.5 text-[10px] font-bold text-text-sub bg-bg-elevated border-b border-border">
            <span>제품</span>
            <span>공급처</span>
            <span className="text-center">이동</span>
          </div>
          {filtered.map((p) => {
            const hasUrl = !!p.product_url;
            const onMove = (e: React.MouseEvent) => {
              e.stopPropagation();
              if (hasUrl) window.open(p.product_url!, "_blank", "noopener,noreferrer");
            };
            const productLabel = p.spec && !p.name.includes(p.spec) ? `${p.name}, ${p.spec}` : p.name;
            return (
              <div
                key={p.id}
                className="grid grid-cols-[2fr_1fr_38px] gap-1.5 items-center px-2 py-2 text-xs border-b border-border last:border-b-0"
              >
                <span className="font-semibold text-text truncate" title={productLabel}>
                  {productLabel}
                </span>
                <span className="text-2xs text-text-sub truncate" title={p.manufacturer_company}>
                  {p.manufacturer_company}
                  {p.premium_yn && <span className="ml-1 text-primary">⭐</span>}
                </span>
                {hasUrl ? (
                  <button
                    onClick={onMove}
                    className="text-[10px] font-bold text-primary hover:underline text-center"
                    title="새 탭에서 제품 페이지 열기"
                  >
                    이동 ↗
                  </button>
                ) : (
                  <span className="text-[10px] text-text-sub/40 text-center">-</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RecipesTab({ recipes }: { recipes: KeywordDetail["recipes"] }) {
  if (recipes.length === 0) {
    return (
      <p className="text-xs text-text-sub text-center py-6">
        관련 레시피가 아직 등록되지 않았어요
      </p>
    );
  }

  // 2026-05-19 v4 — backend 가 이미 제목 매칭 영상만 반환. 분리 라벨 제거 (시그널 카드의 "레시피 N" 이 카운트 보여줌).
  // 옛 (2026-05-03 ~ 05-19): exact / related 2 그룹 분리. 이제 미매칭 영상 자체 없음.

  const RecipeRow = ({ r }: { r: KeywordDetail["recipes"][number] }) => {
    const vid = youtubeId(r.youtube_url);
    return (
      <Link
        key={r.id}
        href={`/recipe/${r.id}`}
        className="flex items-center gap-3 hover:opacity-80 transition-opacity"
      >
        {vid && (
          <img
            src={`https://img.youtube.com/vi/${vid}/mqdefault.jpg`}
            alt={r.title}
            className="w-20 h-14 object-cover rounded-lg flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-text line-clamp-2">{r.title}</p>
          <p className="text-2xs text-text-sub mt-1">
            {r.channel_name && <span>{r.channel_name} · </span>}
            {r.difficulty && <span>난이도 {DIFFICULTY[r.difficulty] ?? r.difficulty} · </span>}
            {r.view_count && <span>{(r.view_count / 10000).toFixed(1)}만회</span>}
          </p>
        </div>
        <span className="text-text-sub text-xs flex-shrink-0">→</span>
      </Link>
    );
  };

  return (
    <div className="space-y-3">
      {recipes.length === 0 ? (
        <p className="text-xs text-text-sub text-center py-6">
          관련 레시피 영상이 아직 없어요
        </p>
      ) : (
        recipes.map((r) => <RecipeRow key={r.id} r={r} />)
      )}
    </div>
  );
}

function DatalabTab({
  data,
  loading,
  stageColor,
}: {
  data: DatalabResponse | null;
  loading: boolean;
  stageColor: string;
}) {
  if (loading) {
    return <p className="text-xs text-text-sub text-center py-6">불러오는 중...</p>;
  }
  if (!data || !data.data || data.data.series.length === 0) {
    return (
      <p className="text-xs text-text-sub text-center py-6">
        네이버 데이터랩에 검색 기록이 없어요.
      </p>
    );
  }

  const { summary } = data.data;
  const dir = summary.direction ? DIRECTION_LABEL[summary.direction] : null;

  // 그래프 자체는 상단 통합 차트가 표시 — DatalabTab 은 요약 카드만 (사용자 결정 2026-05-08)
  return (
    <>
      <div className="grid grid-cols-3 gap-2">
        <div className="text-center py-2 rounded-lg bg-bg-secondary">
          <p className="text-2xs text-text-sub">평균</p>
          <p className="text-base font-bold text-text mt-0.5">
            {summary.avg !== null ? summary.avg.toFixed(1) : "-"}
          </p>
        </div>
        <div className="text-center py-2 rounded-lg bg-bg-secondary">
          <p className="text-2xs text-text-sub">최고</p>
          <p className="text-base font-bold text-text mt-0.5">
            {summary.peak !== null ? summary.peak.toFixed(0) : "-"}
          </p>
        </div>
        <div className="text-center py-2 rounded-lg bg-bg-secondary">
          <p className="text-2xs text-text-sub">추세</p>
          {dir ? (
            <p className="text-base font-bold mt-0.5" style={{ color: dir.color }}>
              {dir.emoji} {dir.label}
            </p>
          ) : (
            <p className="text-base font-bold text-text-sub mt-0.5">-</p>
          )}
        </div>
      </div>

      <p className="text-2xs text-text-sub text-center mt-3">
        * 기간 내 최고 검색량 = 100인 상대 지수 (네이버 데이터랩)
      </p>
    </>
  );
}

/**
 * 쇼핑 탭 — 카운트만 노출 (사용자 결정 2026-05-10, 옵션 B)
 *
 * 옛 버전 (상세 상품 50개 + 검색 + 페이지네이션) 폐기.
 * 사유: 같은 제품 다채널 중복 노출 (예: 커피빈 피스타치오라떼 5개) → 카페 점주에겐 정크.
 *       카운트 자체는 "이 키워드 시장 출시도" 시그널로 가치 있음 → 보존.
 *
 * 클릭 시 네이버 쇼핑 직접 검색으로 외부 이동.
 */
function ShoppingTab({
  data,
  loading,
  keywordText,
}: {
  data: ShoppingResponse | null;
  loading: boolean;
  keywordText: string;
}) {
  if (loading) {
    return <p className="text-xs text-text-sub text-center py-6">불러오는 중...</p>;
  }
  if (!data || data.current_count === null) {
    return (
      <p className="text-xs text-text-sub text-center py-6">
        네이버 쇼핑에 관련 상품이 없어요.
      </p>
    );
  }

  const naverShopUrl = `https://search.shopping.naver.com/search/all?query=${encodeURIComponent(keywordText)}`;

  return (
    <>
      <div className="py-4 text-center rounded-lg bg-bg-secondary">
        <p className="text-2xs text-text-sub">현재 네이버 쇼핑 관련 상품 수</p>
        <p className="text-3xl font-black text-text mt-1">
          {data.current_count.toLocaleString()}
          <span className="text-sm font-normal text-text-sub ml-1">개</span>
        </p>
        <p className="text-2xs text-text-sub mt-2 leading-relaxed px-3">
          시장에 이 키워드 관련 상품이 얼마나 출시됐는지 보여주는 시그널이에요.
        </p>
      </div>

      <a
        href={naverShopUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full mt-3 py-2.5 text-center text-xs font-semibold text-primary border border-primary/40 rounded-lg hover:bg-primary/5 transition-colors"
      >
        네이버 쇼핑에서 직접 보기 →
      </a>
    </>
  );
}

function CommunityTab({
  posts,
  stageColor,
  onMore,
}: {
  posts: KeywordDetail["community_preview"];
  stageColor: string;
  onMore: () => void;
}) {
  if (posts.length === 0) {
    return (
      <>
        <p className="text-xs text-text-sub text-center py-6">
          아직 이 키워드 관련 후기가 없어요
        </p>
        <button onClick={onMore} className="w-full mt-2 py-2 text-2xs text-primary">
          커뮤니티에서 직접 작성하기 →
        </button>
      </>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="border-l-2 pl-3" style={{ borderColor: stageColor }}>
            <p className="text-xs text-text leading-relaxed">{p.content}</p>
            <p className="text-2xs text-text-sub mt-1">
              {new Date(p.created_at).toLocaleDateString("ko-KR")}
            </p>
          </div>
        ))}
      </div>
      <button
        onClick={onMore}
        className="w-full mt-3 py-2 text-2xs text-primary"
      >
        커뮤니티에서 더 보기 →
      </button>
    </>
  );
}

/* ───────────── AI 판단 요약 카드 ───────────── */

interface AiSummaryResponse {
  keyword_id: number;
  keyword: string;
  // 옛 호환 (tldr / judgment_basis 매핑)
  summary: string;
  context: string | null;
  data_summary: string | null;
  // 새 3 블록 (사용자 결정 2026-05-08)
  tldr: string | null;             // 자동 노출
  judgment_basis: string | null;   // 자세히
  shop_direction: string | null;   // 자세히
  model: string;
  tier: string;
  remaining: number | null;
  cached: boolean;
}

function AiSummaryCard({ keywordId }: { keywordId: string }) {
  const { token, isLoggedIn, user } = useAuthStore();
  const beta = useBetaStatus();
  const [data, setData] = useState<AiSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const requestedRef = useRef(false);
  // 베타 활성 동안 모두 풀, 종료 후 paid 만 자세히 (판단 근거 + 매장 방향성) 접근
  // 사용자 결정 2026-05-22 — tldr 노출 + 자세히 잠금
  const betaActive = beta?.beta_active ?? true;
  const isPaidLike = !!user && (user.tier === "paid" || user.tier === "franchise");
  const hasDetailAccess = betaActive || isPaidLike;

  const run = useCallback(async () => {
    if (loading) return;
    if (!isLoggedIn || !token) {
      setError("🔒 로그인 후 이용 가능해요");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get<AiSummaryResponse>(`/api/trends/${keywordId}/ai-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { detail?: string } } };
      const status = e?.response?.status;
      if (status === 401) setError("🔒 로그인 후 이용 가능해요");
      else if (status === 403) setError(e?.response?.data?.detail ?? "AI 기능 이용 권한이 없어요");
      else if (status === 429) setError(e?.response?.data?.detail ?? "오늘 사용량을 모두 썼어요");
      else setError(e?.response?.data?.detail ?? "AI 판단 생성 실패");
    } finally {
      setLoading(false);
    }
  }, [loading, isLoggedIn, token, keywordId]);

  // 자동 호출 (사용자 결정 2026-05-08) — 페이지 진입 시 1회. 캐시 hit 시 즉시 표시 + 쿼터 차감 X
  useEffect(() => {
    if (requestedRef.current) return;
    if (!isLoggedIn || !token) return;
    requestedRef.current = true;
    run();
  }, [isLoggedIn, token, run]);

  return (
    <section className="card p-4 bg-primary/5 border-primary/20">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-bold text-text">🤖 AI 판단</h2>
        {data && (
          <span className="text-2xs text-text-sub">
            {data.cached ? "오늘자" : data.remaining === null ? "무제한" : `오늘 ${data.remaining}회 남음`}
          </span>
        )}
      </div>

      {/* 비로그인 안내 */}
      {!isLoggedIn && (
        <div className="text-xs text-text-sub py-2">
          <p>🔒 로그인 후 AI 판단을 받아보세요</p>
          <Link href="/login" className="text-primary mt-2 inline-block">로그인 →</Link>
        </div>
      )}

      {/* 로딩 (자동 호출 첫 5~10초) */}
      {loading && !data && (
        <div className="flex items-center gap-2 py-2 text-2xs text-text-sub">
          <span className="inline-block w-2 h-2 rounded-full bg-primary/60 animate-pulse" />
          <span>AI 가 분석 중...</span>
        </div>
      )}

      {/* 에러 */}
      {error && !data && isLoggedIn && (
        <div className="text-xs text-text-sub py-2">
          <p>{error}</p>
          {(error.includes("사용량") || error.includes("권한")) && (
            <Link href="/mypage/subscribe" className="text-primary mt-2 inline-block">
              구독 업그레이드 →
            </Link>
          )}
          {!loading && (
            <button onClick={run} className="text-primary mt-2 ml-3">다시 시도</button>
          )}
        </div>
      )}

      {/* 결과 — 3 블록 (사용자 결정 2026-05-08): tldr 자동 + 근거·방향성 클릭 시 펼침 */}
      {data && (
        <div className="space-y-2">
          {/* tldr — 자동 노출 (현주소 + 한 줄 결론) */}
          <p className="text-xs text-text leading-relaxed whitespace-pre-wrap">
            {data.tldr || data.summary}
          </p>

          {/* 자세히 — judgment_basis + shop_direction (옛 데이터 호환: context 단일 fallback) */}
          {(data.judgment_basis || data.shop_direction || data.context) && hasDetailAccess && (
            <>
              {expanded ? (
                <div className="pt-2 border-t border-border space-y-3">
                  {data.judgment_basis && (
                    <div>
                      <p className="text-2xs font-bold text-primary mb-1">판단 근거</p>
                      <p className="text-xs text-text leading-relaxed whitespace-pre-wrap">
                        {data.judgment_basis}
                      </p>
                    </div>
                  )}
                  {data.shop_direction && (
                    <div>
                      <p className="text-2xs font-bold text-primary mb-1">매장 방향성</p>
                      <p className="text-xs text-text leading-relaxed whitespace-pre-wrap">
                        {data.shop_direction}
                      </p>
                    </div>
                  )}
                  {/* 옛 호환 — judgment_basis 없으면 context 표시 */}
                  {!data.judgment_basis && !data.shop_direction && data.context && (
                    <div>
                      <p className="text-2xs font-bold text-primary mb-1">자세히</p>
                      <p className="text-xs text-text leading-relaxed whitespace-pre-wrap">
                        {data.context}
                      </p>
                    </div>
                  )}
                  <button onClick={() => setExpanded(false)} className="text-2xs text-primary">
                    접기 ↑
                  </button>
                </div>
              ) : (
                <button onClick={() => setExpanded(true)} className="text-2xs text-primary">
                  자세히 보기 →
                </button>
              )}
            </>
          )}

          {/* free_auth (베타 종료 후) — 자세히 잠금 */}
          {(data.judgment_basis || data.shop_direction || data.context) && !hasDetailAccess && (
            <Link
              href="/mypage/subscribe"
              className="block pt-2 border-t border-border"
            >
              <div className="text-2xs text-primary font-semibold flex items-center gap-1.5">
                🔒 구독하면 판단 근거 + 매장 방향성 보기 →
              </div>
            </Link>
          )}
        </div>
      )}
    </section>
  );
}
